import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import StandardDropdown from 'Common/Components/StandarDropdown';
import StyledCheckbox from 'Common/Components/StyledCheckbox';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import ToolParams from 'Screens/Automation/Agents/components/AgentDetail/components/ToolParams';
import { saveSchedule, resetTagsInScheduler } from 'store/Schedule/actions';
import { selectCloudAgents, selectUserAgents, selectSchedulerSelected, selectSelectedSection, selectSchedulerAssetsTags, selectSchedulerVulnsTags, selectSchedulerServicesTags } from 'store/Schedule/selectors';
import CronExpression from './components/CronExpression';
import WorkspacesSelector from './components/WorkspacesSelector';
import {
  Wrapper, WrapperTitle, Title, ButtonsWrapper, DescriptionSection,
  Label, Description, Subtitle, WrapperToolParams, WrapperAgent, AgentSection,
  Content, CancelButton, AcceptButton
} from './styled';
import TagsSection from './components/TagsSection';
import AgentsDropdown from './components/AgentsDropdown';
import AgentParams from './components/AgentsParams';
import ModalWrapper from 'Common/Components/ReduxModal/styled';
import { closeModal } from 'store/modals/actions';
import { MODAL_SCHEDULE_CREATE_EDIT } from 'store/modals/modals';
import ExecutionParams from 'Screens/Automation/Agents/components/AgentDetail/components/ExecutionParams';
import CreationWarning from 'Common/Components/CreationWarning';
import useModalShortcuts from 'Hooks/useModalShortcuts';

const CreateEditSchedule = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const schedule = useSelector(selectSchedulerSelected);
  const section = useSelector(selectSelectedSection);
  const userAgents = useSelector(selectUserAgents);
  const cloudAgents = useSelector(selectCloudAgents);
  const [description, setDescription] = useState();
  const [active, setActive] = useState(true);
  const [currentAgent, setCurrentAgent] = useState();
  const [currentTool, setCurrentTool] = useState({});
  const [currentToolParams, setCurrentToolParams] = useState({});
  const [crontab, setCrontab] = useState({ expression: get(schedule, 'value.crontab', '') });
  const [severityRange, setSeverityRange] = useState({
    min: { value: 0, severity: 'unclassified' },
    max: { value: 5, severity: 'critical' }
  });
  const tools = get(currentAgent, 'value.executors', []);
  const mappedTools = tools.map((t) => ({ name: t.name, desc: t.name }));
  const [scheduleWorkspaces, setScheduleWorkspaces] = useState([]);
  const [targetError, setTargetError] = useState('');
  const parsedAgents = () => {
    if ((section === 'scheduledAgents') && !isEmpty(userAgents)) return userAgents.map((t) => ({ label: get(t, 'name', ''), value: t }));
    else if ((section === 'scheduledCloudAgents') && !isEmpty(cloudAgents)) return cloudAgents.map((t) => ({ label: get(t, 'name', ''), value: t }));
    else return [];
  };
  const [showErrorModal, setShowErrorModal] = useState(false);
  const createWarningMessage = intl.formatMessage({ id: 'modal.createVuln.cantCreate.message' });
  const selectionLabelId = section === 'scheduledCloudAgents'
    ? 'schedule.createEditSchedule.securityScannerSelection'
    : 'schedule.createEditSchedule.runnerSelection';
  const dropdownPlaceholderId = section === 'scheduledCloudAgents'
    ? 'schedule.createEditSchedule.dropdownSecurityScanner.placeholder'
    : 'schedule.createEditSchedule.dropdownRunner.placeholder';

  const assetsTags = useSelector(selectSchedulerAssetsTags);
  const vulnsTags = useSelector(selectSchedulerVulnsTags);
  const servicesTags = useSelector(selectSchedulerServicesTags);

  const onChangeAgent = (obj) => {
    setCurrentAgent(obj);
    setCurrentTool({});
    setCurrentToolParams({});
  };

  const onChangeTool = (field, value) => {
    const tool = get(currentAgent, 'value.executors', get(schedule, 'value.agent.executors')).find((t) => t.name === value);
    setCurrentTool(tool);
  };

  const onChangeField = (field, value) => {
    const newParams = { ...currentToolParams };
    newParams[field] = value;
    if (!trim(value)) delete newParams[field];
    setCurrentToolParams(newParams);
  };

  const validateUrlTarget = (domain) => {
    // eslint-disable-next-line prefer-regex-literals
    const re = new RegExp(/^(?!:\/\/)([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,63}$/);
    if (domain && (!re.test(domain))) setTargetError('Target should be a domain. e.g.: domain.com');
    else setTargetError('');
  };

  const onChangeFieldCloud = (field, value, type) => {
    if (type === 'url') validateUrlTarget(value);
    const newParams = { ...currentToolParams, [field]: value };
    if (type === 'list') {
      const newValue = value.replace(/\s+/g, '');
      newParams[field] = newValue.split(',');
    }
    if (value === '') delete newParams[field];
    setCurrentToolParams(newParams);
  };

  const disableAccept = () => {
    const currentToolIsInvalid = () => isEmpty(currentTool);
    const currentToolParamsIsInvalid = () => {
      if (!isEmpty(currentTool.parameters_metadata) && isEmpty(currentToolParams)) return true;
      if (isEmpty(currentTool)) return true;
      if (isEmpty(currentTool.parameters_metadata)) return false;

      const params = currentTool.parameters_metadata;
      return Object.keys(params).some((p) => params[p].mandatory && isEmpty(currentToolParams[p]));
    };

    const cloudParamsIsInvalid = () => {
      if (section === 'scheduledCloudAgents') {
        const params = currentAgent?.value.params;
        return !isEmpty(params) && Object.keys(params).some((p) => params[p].mandatory && isEmpty(currentToolParams[p]));
      }
    };

    if (section === 'scheduledAgents' && (isEmpty(currentAgent) || !description || currentToolIsInvalid() || currentToolParamsIsInvalid() || isEmpty(scheduleWorkspaces))) return true;
    if (section === 'scheduledCloudAgents' && (isEmpty(currentAgent) || !description || cloudParamsIsInvalid() || isEmpty(scheduleWorkspaces))) return true;

    return false;
  };

  const onClose = () => dispatch(closeModal(MODAL_SCHEDULE_CREATE_EDIT));

  const onAccept = () => {
    const agentType = section === 'scheduledAgents' ? 'agent' : 'cloud_agent';
    const tags = { host_tag: assetsTags, vuln_tag: vulnsTags, service_tag: servicesTags };
    if (!disableAccept()) {
      const workspacesNames = scheduleWorkspaces.map((ws) => {
        if (ws.name) {
          return ws.name;
        } else {
          return ws;
        }
      });
      dispatch(saveSchedule(0, currentAgent.value.id, crontab.name, active, crontab.expression, description, currentTool.id, currentToolParams, workspacesNames, Intl.DateTimeFormat().resolvedOptions().timeZone, tags, agentType, severityRange));
      onClose();
    } else setShowErrorModal(true);
  };

  useModalShortcuts({
    id: 'schedule-create-edit',
    onSave: onAccept,
    onCancel: onClose,
    canSave: !disableAccept(),
    saveLabel: intl.formatMessage({ id: 'schedule.createEditSchedule.accept' })
  });

  const title = intl.formatMessage({ id: 'schedule.createEditSchedule.create.title' });

  const onChangeActive = () => {
    setActive(!active);
  };

  useEffect(() => () => {
    dispatch(resetTagsInScheduler());
  }, [dispatch]);

  return (
    <ModalWrapper>
      <Wrapper>
        <WrapperTitle>
          <Title>{ title }</Title>
          <ButtonsWrapper>
            <CancelButton id="scheduler-modal-cancel" onClick={ onClose }>{ intl.formatMessage({ id: 'schedule.createEditSchedule.cancel' }) }</CancelButton>
            <AcceptButton id="scheduler-modal-accept" onClick={ onAccept }>{ intl.formatMessage({ id: 'schedule.createEditSchedule.accept' }) }</AcceptButton>
          </ButtonsWrapper>
        </WrapperTitle>

        <Content>
          <DescriptionSection>
            <Label>{ intl.formatMessage({ id: 'schedule.createEditSchedule.description' }) }</Label>
            <Description id="scheduler-modal-description" onChange={ (e) => setDescription(e.target.value) } value={ description } placeholder={ intl.formatMessage({ id: 'schedule.createEditSchedule.description.placeholder' }) } />
            <StyledCheckbox id="scheduler-modal-active" checked={ active } label={ intl.formatMessage({ id: 'schedule.createEditSchedule.active' }) } onChange={ onChangeActive } />
          </DescriptionSection>

          <CronExpression crontab={ crontab } onChangeCrontab={ (value) => setCrontab(value) } isCreateSchedule />

          <AgentSection>
            <Subtitle>{ intl.formatMessage({ id: selectionLabelId }) }</Subtitle>
            <WrapperAgent>
              <AgentsDropdown id="scheduler-modal-agent" options={ parsedAgents() } updateValue={ onChangeAgent } value={ currentAgent } placeholder={ intl.formatMessage({ id: dropdownPlaceholderId }) } required />
            </WrapperAgent>

            { (!isEmpty(currentAgent) || !isEmpty(get(schedule, 'value.agent', null))) &&
              (<>

              { section === 'scheduledCloudAgents'
                ? <>
                  <WrapperToolParams isCloud={ currentAgent?.value?.type === 'cloud_agent' }>
                      <ExecutionParams currentTool={ currentAgent } cloudScheduler />
                      <AgentParams currentAgent={ currentAgent } onChangeField={ onChangeFieldCloud } targetError={ targetError } defaultParams={ currentToolParams } />
                  </WrapperToolParams>
                  </>
                : <>
                    <Subtitle>{ intl.formatMessage({ id: 'schedule.createEditSchedule.toolSelection' }) }</Subtitle>
                    <StandardDropdown id="scheduler-modal-tool" defaultValue={ get(currentTool, 'name', '') } width="200px" field="toolParams" options={ mappedTools } updateValue={ onChangeTool } placeholder={ intl.formatMessage({ id: 'schedule.createEditSchedule.dropdownTool.placeholder' }) } />
                    { currentTool?.name
                      ? <WrapperToolParams isCloud={ currentAgent?.value?.type === 'cloud_agent' }>
                          <ToolParams
                            currentTool={ currentTool }
                            defaultParams={ currentToolParams }
                            onChangeField={ onChangeField }
                            severityRange={ severityRange }
                            setSeverityRange={ setSeverityRange }
                          />
                        </WrapperToolParams>
                      : null
                    }
                  </>
                }

               </>
              ) }
          </AgentSection>
          <WorkspacesSelector scheduleWorkspaces={ scheduleWorkspaces } setScheduleWorkspaces={ setScheduleWorkspaces } />
          <TagsSection isCreateSchedule />
        </Content>
      </Wrapper>
      { showErrorModal && <CreationWarning errorTitle={ title } errorMessage={ createWarningMessage } onClose={ () => setShowErrorModal(false) } /> }
    </ModalWrapper>
  );
};

export default CreateEditSchedule;
